import React from 'react';
import { motion } from 'framer-motion';
import { FaLinkedin, FaInstagram } from 'react-icons/fa6';
import { MdEmail } from 'react-icons/md';
import './About.css';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1] // Apple-style ease
    }
  }
};

function About() {
  return (
    <div className="about-section" id="about">
      <div className="background-decorations">
        <div className="decoration-orb orb-1"></div>
        <div className="decoration-orb orb-2"></div>
      </div>

      <div className="content-wrapper">
        <motion.div
          className="about-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true, amount: 0.8 }}
        >
          <h2 className="section-label">About Me</h2>
          <h1 className="section-title">Bridging Data & Operations</h1>
        </motion.div>

        <motion.div
          className="bento-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {/* 1. Profile Card (Large 2x2) */}
          <motion.div className="bento-card profile-card" variants={cardVariants}>
            <div className="profile-content">
              <div className="profile-image-wrapper">
                <img src="zs.jpg" alt="ZhaoSheng" className="profile-image" />
              </div>
              <div className="profile-info">
                <h3>Tang ZhaoSheng</h3>
                <p className="role-text">Site Reliability Engineer</p>
                <div className="location-badge">
                  📍 Singapore
                </div>
                {/* Status Removed as per request */}
              </div>
            </div>
          </motion.div>

          {/* 2. Experience (Large 2x2) */}
          <motion.div className="bento-card experience-card" variants={cardVariants}>
            <div className="card-Label">
              Experience
            </div>
            <div className="info-details">
              <div className="info-block">
                <div className="info-header">
                  <h4>Site Reliability Engineer</h4>
                  <span className="info-year">Oct 2025 - Present</span>
                </div>
                <p className="info-sub">Huawei Singapore</p>
                <ul className="info-list">
                  <li>Responsible for Big Data Platform operation & maintenance.</li>
                </ul>
              </div>

              <div className="info-divider"></div>

              <div className="info-block">
                <div className="info-header">
                  <h4>DevOps Engineer</h4>
                  <span className="info-year">Aug 2023 - Jul 2024</span>
                </div>
                <p className="info-sub">GoCode Pte Ltd (Singapore)</p>
                <ul className="info-list">
                  <li>CI/CD Pipeline Architecture, K8s Clustering.</li>
                </ul>
              </div>

              <div className="info-divider"></div>

              <div className="info-block">
                <div className="info-header">
                  <h4>Data Eng Intern</h4>
                  <span className="info-year">Jan 2022 - May 2022</span>
                </div>
                <p className="info-sub">ST Engineering</p>
                <ul className="info-list">
                  <li>Data Cleaning, ETL Development.</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* 3. Bio Card (Wide 2x1) */}
          <motion.div className="bento-card bio-card" variants={cardVariants}>
            <div className="card-Label">
              Bio
            </div>
            <p className="bio-text">
              I am currently a <span className="highlight">SRE at Huawei</span>, specializing in the O&M of large-scale Big Data platforms.
              <br /><br />
              My professional background sits at the intersection of infrastructure and data intelligence. With a <span className="highlight">Master’s degree in Data Science</span> from NUS and prior experience as a <span className="highlight">DevOps Engineer</span>, I possess a unique ability to understand both the underlying infrastructure (Kubernetes, Docker, CI/CD) and the data workloads running on top of it.
              <br /><br />
              Currently, I focus on ensuring the high availability, scalability, and performance of critical data systems. My goal is to bridge the gap between complex data pipelines and stable infrastructure operations.
            </p>
          </motion.div>

          {/* 4. Skills (Wide 2x1) */}
          <motion.div className="bento-card skills-card" variants={cardVariants}>
            <div className="card-Label">
              Tech Stack
            </div>
            <div className="skills-wrapper">
              <div className="skill-category">
                <h5>Languages</h5>
                <div className="tags">
                  <span>Python</span><span>SQL</span><span>Shell</span>
                </div>
              </div>
              <div className="skill-category">
                <h5>Big Data</h5>
                <div className="tags">
                  <span>Hadoop</span><span>Spark</span><span>Flink</span><span>MapReduce</span><span>Tableau</span>
                </div>
              </div>
              <div className="skill-category">
                <h5>Middleware</h5>
                <div className="tags">
                  <span>Kafka</span><span>Pulsar</span><span>Redis</span>
                </div>
              </div>
              <div className="skill-category">
                <h5>DevOps</h5>
                <div className="tags">
                  <span>Docker</span><span>K8s</span><span>AWS</span><span>Jenkins</span>
                </div>
              </div>
              <div className="skill-category">
                <h5>Web & DB</h5>
                <div className="tags">
                  <span>React</span><span>MySQL</span><span>PostgreSQL</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* 5. Education (Wide 3 cols) */}
          <motion.div className="bento-card education-card" variants={cardVariants}>
            <div className="card-Label">
              Education
            </div>
            <div className="info-details">
              <div className="info-block">
                <div className="info-header">
                  <h4>National University of Singapore</h4>
                  <span className="info-year">Aug 2024 – May 2025</span>
                </div>
                <p className="info-sub">Master of Science - Data Science for Sustainability</p>
                <ul className="info-list">
                  <li>Specialized in Data analysis, modelling and visualization</li>
                </ul>
              </div>

              <div className="info-divider"></div>

              <div className="info-block">
                <div className="info-header">
                  <h4>Nanyang Technological University</h4>
                  <span className="info-year">Aug 2020 – May 2023</span>
                </div>
                <p className="info-sub">Bachelor of Engineering - (Information Engineering & Media)</p>
                <p className="info-meta">with Honours (Merit)</p>
                <ul className="info-list">
                  <li>Developed a strong foundation in software development and web technologies through coursework and projects</li>
                  <li>Gained expertise in programming languages such as Python, with practical experience in data science techniques</li>
                  <li>Acquired knowledge in AI, including Machine Learning algorithms, Deep Learning, and Computer Vision</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Stacked Side Column: Hobbies & Socials */}
          <motion.div
            className="sidebar-stack"
            variants={containerVariants} // Note: Treating stack as container might affect staggered children inside, or just use div if animation is handled on individ cards
          >
            {/* 6. Hobbies */}
            <motion.div className="bento-card hobbies-card" variants={cardVariants}>
              <div className="card-Label">
                Interests
              </div>
              <div className="hobbies-grid">
                <div className="hobby-item">Photography</div>
                <div className="hobby-item">Hiking</div>
                <div className="hobby-item">Reading</div>
                <div className="hobby-item">Gaming</div>
              </div>
            </motion.div>

            {/* 7. Socials */}
            <motion.div className="bento-card social-card" variants={cardVariants}>
              <div className="card-Label-small">Contact</div>
              <div className="social-icons-wrapper">
                <a href="https://www.linkedin.com/in/tang-zhaosheng-4408021b7/" target="_blank" rel="noopener noreferrer" className="social-icon">
                  <FaLinkedin size={28} />
                </a>
                <a href="https://www.instagram.com/tongsiusing/" target="_blank" rel="noopener noreferrer" className="social-icon">
                  <FaInstagram size={28} />
                </a>
                <a href="mailto:siusingtong@yahoo.com" className="social-icon">
                  <MdEmail size={28} />
                </a>
              </div>
            </motion.div>
          </motion.div>

        </motion.div>
      </div>
    </div>
  );
}

export default About;